/**
 * --------------------------------------------------------------------------
 * toggles.js
 * Toggle and mobile responsive functionality
 * --------------------------------------------------------------------------
 */

import {
  onDOMContentLoaded
} from 'bootstrap/js/src/util/index.js'
import EventHandler from 'bootstrap/js/src/dom/event-handler.js'
import SelectorEngine from 'bootstrap/js/src/dom/selector-engine.js'
import { uoeUID } from './helpers.js'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

// const NAME = 'toggles'
const CLASS_NAME_JS = 'edgel-js'
const CLASS_NAME_SEARCH_COLLAPSIBLE = 'masthead-logo-collapsible-search'
const SELECTOR_JS_CLASS = 'html'
const SELECTOR_UOE_NAV = '.uoe-nav ul, .uoe-nav-combined ul'
const SELECTOR_SEARCH = '.masthead-search'
const SELECTOR_MENU_COLLAPSIBLE = '.uoe-nav-link-expanded ~ ul, .uoe-nav-link-collapsed ~ ul'
const SELECTOR_MASTHEAD = '.masthead'
const SELECTOR_ROW = '.row'
const TEXT_SEARCH_TOGGLE = '<span class="visually-hidden">Show/hide site search</span>'
const TEXT_MENU_TOGGLE = '<span class="visually-hidden">Toggle Navigation</span> Menu'
const TEXT_MENU_COLLAPSE_TOGGLE = '<span class="visually-hidden">Toggle menu items</span>'

/**
 * Public methods
 */

const MobileToggles = {
  addIcons(selector, icon, iconSelector) {
    const _addIcons = function () {
      const selectorElements = SelectorEngine.find(this.selector)
      for (const [i, element] of selectorElements.entries()) {
        if (!SelectorEngine.findOne(this.iconSelector, element)) {
          element.prepend(this.icon)
          if (i !== selectorElements.length - 1) {
            this.icon = this.icon.cloneNode(true)
          }
        }
      }
    }.bind({ selector, icon, iconSelector })

    onDOMContentLoaded(_addIcons)
  },
  addJsClass() {
    const _addJsClass = function () {
      const selectorElement = this.selector === 'html' ? document.documentElement : SelectorEngine.findOne(this.selector)

      if (selectorElement) {
        selectorElement.classList.add(this.className)
      }
    }.bind({
      selector: SELECTOR_JS_CLASS,
      className: CLASS_NAME_JS
    })

    onDOMContentLoaded(_addJsClass)
  },
  addNavToggle() {
    const _addNavToggle = function () {
      const toggle = document.createElement('button')
      toggle.className = 'btn btn-uoe-nav-toggler btn-hamburger'
      toggle.setAttribute('type', 'button')
      toggle.setAttribute('aria-expanded', 'false')
      toggle.dataset.bsToggle = 'hamburger'
      toggle.innerHTML = this.TEXT_MENU_TOGGLE

      const element = SelectorEngine.findOne(this.selector)
      if (element) {
        element.classList.add('collapse')

        element.id ||= this.uoeUID()

        toggle.dataset.bsTarget = `#${element.id}`
        toggle.setAttribute('aria-controls', element.id)
        element.before(toggle)
      }
    }.bind({
      selector: SELECTOR_UOE_NAV,
      uoeUID,
      TEXT_MENU_TOGGLE
    })

    onDOMContentLoaded(_addNavToggle)
  },
  addSearchToggle() {
    const _addSearchToggle = function () {
      const searchBlock = SelectorEngine.findOne(this.SELECTOR_SEARCH)
      if (!searchBlock) {
        return
      }

      const toggle = document.createElement('button')
      toggle.setAttribute('type', 'button')
      toggle.dataset.bsToggle = 'collapse'
      toggle.className = 'btn btn-masthead-search-toggler'
      toggle.innerHTML = this.TEXT_SEARCH_TOGGLE

      const row = searchBlock.closest(this.SELECTOR_ROW)
      if (!row || !row.closest(this.SELECTOR_MASTHEAD)) {
        throw new Error('Please use Bootstrap row and col classes in masthead')
      }

      // Make masthead-search collapsible
      const element = SelectorEngine.findOne(this.SELECTOR_SEARCH)
      if (element) {
        element.classList.add('collapse')

        element.id ||= this.uoeUID()

        toggle.dataset.bsTarget = `#${element.id}`
        toggle.setAttribute('aria-controls', element.id)

        // Add padding to move the logo away from the search toggle button
        const insertPoint = row.firstElementChild
        insertPoint.classList.add(this.CLASS_NAME_SEARCH_COLLAPSIBLE)
        insertPoint.after(toggle)
      }
    }.bind({
      uoeUID,
      TEXT_SEARCH_TOGGLE,
      SELECTOR_ROW,
      SELECTOR_MASTHEAD,
      CLASS_NAME_SEARCH_COLLAPSIBLE,
      SELECTOR_SEARCH
    })

    onDOMContentLoaded(_addSearchToggle)
  },
  addMenuToggles() {
    const _addMenuToggles = function () {
      const collapsibles = SelectorEngine.find(this.SELECTOR_MENU_COLLAPSIBLE)
      if (!collapsibles.length) {
        return
      }

      collapsibles.forEach(ul => {
        ul.id ||= this.uoeUID()
        const link = SelectorEngine.findOne(':scope > a.uoe-nav-link', ul.parentElement)
        if (!link) {
          return
        }

        const expanded = link.classList.contains('uoe-nav-link-expanded')
        const toggle = document.createElement('button')
        toggle.setAttribute('type', 'button')
        toggle.dataset.bsToggle = 'collapse'
        toggle.dataset.bsTarget = `#${ul.id}`
        toggle.setAttribute('aria-controls', ul.id)
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
        toggle.className = 'btn btn-uoe-nav-link'
        toggle.innerHTML = this.TEXT_MENU_COLLAPSE_TOGGLE

        ul.classList.add('collapse')
        if (expanded) {
          ul.classList.add('show')
        }

        ul.before(toggle)

        EventHandler.on(ul, 'shown.bs.collapse', event => {
          const link = SelectorEngine.findOne(':scope > a.uoe-nav-link', event.target.parentElement)
          if (link) {
            link.classList.remove('uoe-nav-link-collapsed')
            link.classList.add('uoe-nav-link-expanded')
          }
        })
        EventHandler.on(ul, 'hidden.bs.collapse', event => {
          const link = SelectorEngine.findOne(':scope > a.uoe-nav-link', event.target.parentElement)
          if (link) {
            link.classList.remove('uoe-nav-link-expanded')
            link.classList.add('uoe-nav-link-collapsed')
          }
        })
      })
    }.bind({
      uoeUID,
      SELECTOR_MENU_COLLAPSIBLE,
      TEXT_MENU_COLLAPSE_TOGGLE
    })

    onDOMContentLoaded(_addMenuToggles)
  }
}

/**
 * Add EdGEL toggle buttons
 */
MobileToggles.addJsClass()
MobileToggles.addNavToggle()
MobileToggles.addSearchToggle()
MobileToggles.addMenuToggles()

export default MobileToggles
